/* eslint-disable camelcase */
import React, { FC } from 'react';
import { Grid, Typography, useTheme } from '@mui/material';
import { getContrastRatio } from '@mui/material/styles';
import { Luci_Shopping_Api_Responses_GetStoreCustomerRewardsByGuidResponse } from '../../types/api/v7.1/models/Luci_Shopping_Api_Responses_GetStoreCustomerRewardsByGuidResponse';
import Styles from '../../pages/InvoicePortalConfirmation/InvoicePortalConfirmation.module.scss';

const RewardsPanel: FC<
  Luci_Shopping_Api_Responses_GetStoreCustomerRewardsByGuidResponse & {
    totalPointsAccruedOnThisOrder: number;
  }
> = ({
  totalPointsAccruedOnThisOrder,
  pointsAvailable,
  rewardsAvailable,
  rewardPointGoal,
  pointsNeededForNextReward,
}) => {
  const theme = useTheme();

  // Pick whichever of white or black-87% has the higher contrast ratio against
  // the rewards background (secondary.main), so the foreground is as legible as
  // possible for every tenant and in both modes. MUI's built-in `contrastText`
  // only targets 3:1, which leaves the mid-tone pinks (Paparazzi #EC008C/#F03293,
  // Jordan #EA6B8B) hard to read; choosing the higher-contrast of the two does
  // better. This replaces the old hardcoded pink that was invisible on the pink
  // Paparazzi background.
  const rewardsBackground = theme.palette.secondary.light;
  // Use the true black/white poles (`common.*`, present on every MUI theme) as
  // the contrast candidates. The semantic `textStatus.white` / `text.primary`
  // tokens are mid-tones — and undefined on themes without the custom palette —
  // which both dropped some tenants below WCAG-AA 4.5:1 (Wayroo/Jordan light) and
  // crashed when RewardsPanel renders without the tenant theme. `common.black`
  // on the light rewards background wins at >= 5.5:1 for every tenant.
  const textWhite = theme.palette.common.white;
  const textBlack = theme.palette.common.black;
  const rewardsTextColor =
    getContrastRatio(rewardsBackground, textWhite) >=
    getContrastRatio(rewardsBackground, textBlack)
      ? textWhite
      : textBlack;

  // Helper function to handle pluralization
  function isPlural(quantity: number, text: string): string {
    return quantity !== 1 ? `${text}s` : text;
  }

  function customerPointsAvailable(): boolean {
    return pointsAvailable !== undefined && pointsAvailable > 0;
  }

  return (
    <Grid
      item
      xs={12}
      paddingTop="1rem"
      sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
    >
      <Typography component="h3" variant="h4" marginBottom=".5rem">
        <strong>Rewards</strong>
      </Typography>
      <section
        className={Styles.rewards}
        data-testid="rewards-section"
        style={{
          background: rewardsBackground,
          // Cascades to the highlight and body text inside the box.
          color: rewardsTextColor,
          marginBottom: '1rem',
        }}
      >
        <div className={Styles.rewardsBody}>
          <Typography
            data-testid="points-earned"
            className={`${Styles.rewardsHighlight} ${Styles.rewardsLargeText}`}
            marginBottom="1rem"
          >
            You have earned {totalPointsAccruedOnThisOrder}{' '}
            {isPlural(totalPointsAccruedOnThisOrder, 'point')} on this order
          </Typography>
          {customerPointsAvailable() && (
            <Typography>
              You now have{' '}
              <span className={Styles.bold}> {rewardsAvailable} </span>{' '}
              {isPlural(rewardsAvailable ?? 0, 'reward')} available!
            </Typography>
          )}
        </div>
      </section>
      <Typography data-testid="rewards-goal">
        <b>
          Buy {rewardPointGoal ?? '?'} {isPlural(rewardPointGoal ?? 1, 'item')}{' '}
          and get 1 FREE.
        </b>
        <br />
        You have earned <b>{totalPointsAccruedOnThisOrder}</b>{' '}
        {isPlural(totalPointsAccruedOnThisOrder, 'point')} on this order.
      </Typography>
      {pointsNeededForNextReward && (
        <Typography data-testid="points-needed" marginBottom="1rem">
          You need{' '}
          <span className={Styles.bold}>{pointsNeededForNextReward}</span>{' '}
          more {isPlural(pointsNeededForNextReward, 'point')} to earn a free
          item.
        </Typography>
      )}
      {/* {customerRewardsURL && (
        <p>
          <a className={Styles.bold} href={customerRewardsURL}>
            View my rewards
          </a>
        </p>
      )} */}
    </Grid>
  );
};

export default RewardsPanel;
